import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { MapContainer, TileLayer, ZoomControl, ScaleControl } from 'react-leaflet';
import withTranslation from 'hoc/withTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { getAreaPolygonFormat } from 'utils/territories/TerritoryAreaConverter';
import { useCookies } from 'react-cookie';
import { isEqual } from 'lodash';
import getCountryISO2 from 'country-iso-3-to-2';
import { PANELS } from 'reducers/user';
import { getLastValidIteration, getToursDataFromIteration } from 'utils/SolutionHelpers';
import { executeFetchTrafficPatterns } from 'utils/trafficHelpers';
import { getBounds, toBounds } from '../../utils/map/MapCalculations';
import config from '../../config';
import MapMarkers from './MapMarkers/MapMarkers';
import MapTourLines from './MapTourLines';
import ActivityConnectionLines from './ActivityConnectionLines';
import { fitMapBounds, getMapSettings } from '../../utils/map/MapUtils';
import { getGeoInfoFromObjects, getOriginalLines } from '../../utils/map/MapTourLineUtils';
import {
  accessLogging,
  TIMEOUT_VALUES,
  getRoutingTourInfo,
  selectUnassigned,
  setTerritoryParameter,
  selectTourById,
  setUserParam,
  setError,
} from '../../actions';
import { tourToRoutingRequest } from '../../utils/RoutingConverter';
import MapAreas from './MapAreas';
import { APP_MODES, getAppMode } from '../../utils/urlHelpers';
import { getTerritoriesGeoJSON } from '../../utils/territories/TerritoriesHelpers';
import LocationSelector from './LocationSelector';
import { isProdEnv } from '../../utils/helpers';
import { getGroupsPerTour, getMapUrl } from '../../utils/MapHelpers';
import {
  generateAllTourConcaveHull,
  generateAllTourConvexHull,
} from '../../utils/territories/TerritoriesFromJson';
import TrafficTimeline from './TrafficTimeline';
import TrafficRouteSegments from './TrafficRouteSegments';
import TrafficLegend from './TrafficLegend';

const {
  map: { url, pviewUrl },
  trafficFactors: { urlPOST, apiKey, GETapiBase },
} = config;
const appMode = getAppMode();
const isProd = isProdEnv();
const terraFF = appMode.includes(APP_MODES.TERRA);
const trafficFacts = appMode.includes(APP_MODES.TRAFFIC_FACTORS);

const MAX_MAP_ZOOM = 22;
const MIN_MAP_ZOOM = 2;
const MAX_NATIVE_ZOOM = 18;

const StyledMap = styled.div({
  width: '100%',
  overflow: 'hidden',
  position: 'fixed',
});

const Map = ({
  mapData,
  oAuth,
  user,
  display,
  routingData,
  isSolution,
  tourData,
  translations,
  tourPlanner,
  solutionId,
}) => {
  const dispatch = useDispatch();
  const mapLayer = useRef(null);
  const currentStep = useSelector((state) => state.usageContext.currentStep);
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const orders = useSelector((state) => state.orders[solution.show]);
  const { showTourConvexHull, showTourConcaveHull, satelliteView } = user.mapSettings;
  const { territories, groupAreas, avoidAreas, excludeAreas } = useSelector(
    (state) => state.areas[solution.show],
  );
  const filtered = getLastValidIteration(solution.requests[solution.show]);
  const tData = useMemo(() => getToursDataFromIteration(filtered), [filtered]);
  const effectiveTourIndex =
    display.routeIds?.length === 1 ? display.routeIds[0] : display.selectedTour;
  const currentTour = useMemo(
    () => (tData?.tours ? tData.tours[effectiveTourIndex] : null),
    [tData, effectiveTourIndex],
  );
  const hideRoutes = user.mapSettings.hideAllRoutes;
  const trafficRequestInProgress = useRef(false);
  const prevSelectedTourRef = useRef(display.selectedTour);
  const activeTourForFetchRef = useRef(display.selectedTour);
  const abortControllerRef = useRef(null);
  const trafficMatrix = user.trafficMatrix;
  const handleSetTerritoryParameter = useCallback(
    (parameter) => dispatch(setTerritoryParameter(parameter)),
    [dispatch],
  );
  const handleSetError = useCallback((data) => dispatch(setError(data)), [dispatch]);
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);
  const handleSelectTourById = useCallback((data) => dispatch(selectTourById(data)), [dispatch]);
  const handleSelectUnassigned = useCallback(() => dispatch(selectUnassigned(true)), [dispatch]);
  const tempOrders = solution.requests[solution.show].tempOrders;
  const handleGetRoutingTourInfo = useCallback(
    (index, tour) =>
      dispatch(
        getRoutingTourInfo({
          oAuth,
          routingRequest: tourToRoutingRequest(
            tour,
            tourPlanner,
            tempOrders || orders,
            null,
            hideRoutes,
          ),
          tourId: index,
          solutionId,
        }),
      ),
    [dispatch, oAuth, tourPlanner, solutionId, orders, tempOrders, hideRoutes],
  );
  const userDistance = user.distance;
  const [areaPolygon, setAreaPolygon] = useState(null);
  const [selectedAreasList, setSelectedAreasList] = useState(null);
  const [routingRoutes, setRoutingRoutes] = useState(null);
  const [originalLocation, setOriginalLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [bounds, setBounds] = useState();
  const [cookies] = useCookies(['apikey']);
  const [basePoliticalViews, setBasePoliticalViews] = useState([]);
  const [selectedTimestamp, setSelectedTimestamp] = useState(null);
  const [showTrafficTimeline, setShowTrafficTimeline] = useState(false);
  const showTraffic = user.trafficFactorsEnabled;
  const ISO2Code = getCountryISO2(user.politicalView);
  const hasPView = basePoliticalViews.includes(ISO2Code);
  const hasApiKey = cookies.apikey && cookies.apikey !== '';
  const allowMarkers = isProd ? hasApiKey : true;
  const [tourHulls, setTourHulls] = useState([]);
  const validStep = [3, 4].includes(currentStep);
  const zoomPosition = user.wizardShown
    ? 'bottomleft'
    : user.jsonPanelOpen
    ? 'topleft'
    : 'topright';

  useEffect(() => {
    if (showTourConvexHull && validStep) {
      setTourHulls(generateAllTourConvexHull(tourData));
    } else if (showTourConcaveHull && showTourConcaveHull.enabled && validStep) {
      setTourHulls(generateAllTourConcaveHull(tourData, showTourConcaveHull.value));
    } else {
      setTourHulls([]);
    }
  }, [tourData, showTourConvexHull, showTourConcaveHull, validStep]);

  const indexes = display.selectedTour !== undefined ? [display.selectedTour] : display.routeIds;
  const groupsToShow = getGroupsPerTour(tourData, indexes, solution);
  const showTourHulls = tourHulls.length > 0 && !display.showUnassigned;
  const showMapAreas = areaPolygon != null && (territories.isEnabled || showTourHulls);

  const showUnassigned = useCallback(() => {
    handleSelectUnassigned();
  }, [handleSelectUnassigned]);

  const getPoliticalViews = async () => {
    axios
      .get(pviewUrl)
      .then((response) => {
        setBasePoliticalViews(response.data.base);
      })
      .catch((error) => {
        console.error('Error: ', error);
      });
  };

  const activateTour = useCallback(
    (index, tour) => {
      if (display.selectedTour !== index) {
        handleSelectTourById({ selectedTour: index, noMapMovement: true });
        handleSelectTourById({ selectOne: index, noMapMovement: true });
        handleGetRoutingTourInfo(index, tour);
      }
    },
    [handleSelectTourById, handleGetRoutingTourInfo, display.selectedTour],
  );

  useEffect(() => {
    if (terraFF) setAreaPolygon(getAreaPolygonFormat(territories.areaDetails));
    else
      setAreaPolygon(
        getTerritoriesGeoJSON(
          territories.areaDetails,
          avoidAreas.areaDetails,
          groupAreas.areaDetails,
          excludeAreas?.areaDetails,
          showTourHulls ? tourHulls : [],
          { ...tourData, tourId: display.selectedTour },
        ),
      );
  }, [
    territories,
    groupAreas,
    avoidAreas,
    excludeAreas,
    tourHulls,
    showTourHulls,
    tourData,
    display.selectedTour,
  ]);

  useEffect(() => {
    const newBounds = getBounds(mapData, user, display, routingRoutes);
    const shouldMove =
      !user.openPanel || !user.openPanel === PANELS.MAP_SETTINGS || display.noMapMovement === false;
    if (
      map &&
      !tourPlanner.editedOrder?.customJobAddress &&
      !isEqual(newBounds, bounds) &&
      shouldMove &&
      !display.noMapMovement
    ) {
      setBounds(newBounds);
      setTimeout(() => {
        fitMapBounds(map, newBounds);
      }, TIMEOUT_VALUES.HIGH);
    }
  }, [
    mapData,
    user,
    display.showProblem,
    display.showUnassigned,
    display.selectedTour,
    display.noMapMovement,
    routingRoutes,
    bounds,
    tourPlanner.editedOrder,
    display,
    map,
  ]);

  useEffect(() => {
    const newBounds = getBounds(mapData, user, display, routingRoutes);

    if (map && !isEqual(newBounds, bounds) && !display.noMapMovement) {
      setBounds(newBounds);
      setTimeout(() => {
        fitMapBounds(map, newBounds);
      }, TIMEOUT_VALUES.HIGH);
    }
  }, [
    mapData,
    user,
    display.showProblem,
    display.showUnassigned,
    display.selectedTour,
    display.noMapMovement,
    routingRoutes,
    bounds,
    tourPlanner.editedOrder,
    display,
    map,
  ]);

  useEffect(() => {
    const routeRequestError = routingData && routingData.requestError;
    const routes =
      routeRequestError || user.mapSettings.showStraightRouteLines
        ? null
        : getGeoInfoFromObjects(routingData);
    setRoutingRoutes(routes);
    const lines = routeRequestError ? null : getOriginalLines(routingData);
    setOriginalLocation(lines);
  }, [routingData, setRoutingRoutes, user.mapSettings.showStraightRouteLines]);

  const handleOnSelect = useCallback(
    (value) => {
      const selectedAreaValue = value.postal_code;
      const newSelectedAreas = { value: selectedAreaValue };
      setSelectedAreasList(newSelectedAreas);
      handleSetTerritoryParameter({ selectedAreas: newSelectedAreas });
    },
    [selectedAreasList, territories, handleSetTerritoryParameter],
  );

  useEffect(() => {
    getPoliticalViews();
  }, []);

  useEffect(() => {
    if (mapLayer.current) {
      mapLayer.current.setUrl(
        getMapUrl(url, translations.map.language, ISO2Code, hasPView, satelliteView),
      );
    }
  }, [hasPView, ISO2Code, translations.map.language, satelliteView]);

  const handleFetchTrafficPatterns = useCallback(
    (departureTimeOffset = 0, silent = false) => {
      const tourAtCallTime = effectiveTourIndex;
      activeTourForFetchRef.current = tourAtCallTime;

      // Abort any in-flight poll from a previous fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (!silent && user.trafficFactorsEnabled && trafficFacts) {
        handleSetUserParam({ trafficFetching: true });
      }

      return executeFetchTrafficPatterns({
        trafficRequestInProgress,
        trafficFF: user.trafficFactorsEnabled,
        trafficFacts,
        solutionRequests: solution.requests,
        solutionShow: solution.show,
        selectedTour: effectiveTourIndex,
        departureTimeOffset,
        urlPOST,
        GETapiBase,
        apiKey,
        signal: controller.signal,
        onSuccess: (factorsData, origins) => {
          if (activeTourForFetchRef.current !== tourAtCallTime) return;
          handleSetUserParam({
            trafficFetching: false,
            trafficMatrix: {
              factorsData,
              origins,
              departureTimeOffset,
            },
          });
        },
        onError: (errorMessage) => {
          handleSetUserParam({ trafficFetching: false });
          handleSetError({ errorMessage });
        },
      });
    },
    [
      solution.requests,
      solution.show,
      effectiveTourIndex,
      handleSetUserParam,
      handleSetError,
      trafficFacts,
      user.trafficFactorsEnabled,
    ],
  );

  const handleFetchTrafficPatternsRef = useRef(handleFetchTrafficPatterns);
  handleFetchTrafficPatternsRef.current = handleFetchTrafficPatterns;

  // Auto-disable traffic factors when leaving tour details view
  useEffect(() => {
    if (display.selectedTour === undefined && user.trafficFactorsEnabled) {
      handleSetUserParam({
        trafficFactorsEnabled: false,
        trafficMatrix: null,
        trafficTimeOffset: 0,
        trafficFetching: false,
      });
      setSelectedTimestamp(null);
      setShowTrafficTimeline(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display.selectedTour]);

  useEffect(() => {
    if (currentTour && currentTour.stops && currentTour.stops.length > 0) {
      const tourChanged = prevSelectedTourRef.current !== effectiveTourIndex;
      prevSelectedTourRef.current = effectiveTourIndex;

      trafficRequestInProgress.current = false;

      if (tourChanged) {
        handleSetUserParam({ trafficMatrix: null, trafficTimeOffset: 0, trafficFetching: false });
        setSelectedTimestamp(null);
        setShowTrafficTimeline(false);
      }

      handleFetchTrafficPatternsRef.current(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTour, effectiveTourIndex]);

  useEffect(() => {
    if (user.trafficFactorsEnabled && currentTour?.stops?.length > 0) {
      trafficRequestInProgress.current = false;

      const alreadyHasData = !!user.trafficMatrix;
      handleFetchTrafficPatternsRef.current(0, alreadyHasData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.trafficFactorsEnabled]);

  useEffect(() => {
    const offset = user.trafficTimeOffset;

    if (offset !== undefined && offset !== null && offset !== 0) {
      handleFetchTrafficPatternsRef.current(offset, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.trafficTimeOffset]);

  useEffect(() => {
    if (trafficMatrix?.factorsData?.matrix?.timestamps) {
      const { timestamps } = trafficMatrix.factorsData.matrix;
      if (timestamps.length > 0) {
        const offset = user.trafficTimeOffset;
        const originalTime = currentTour?.stops?.find((s) => s.time?.arrival)?.time?.arrival;
        if (offset && originalTime) {
          const targetTime = new Date(originalTime).getTime() + offset;
          let closest = 0;
          let minDiff = Infinity;
          timestamps.forEach((ts, i) => {
            const diff = Math.abs(new Date(ts).getTime() - targetTime);
            if (diff < minDiff) {
              minDiff = diff;
              closest = i;
            }
          });
          setSelectedTimestamp(timestamps[closest]);
        } else {
          setSelectedTimestamp(timestamps[0]);
        }
        setShowTrafficTimeline(true);
      }
    }
  }, [trafficMatrix]);

  return (
    <>
      <StyledMap user={user} translations={translations}>
        <MapContainer
          attributionControl={false}
          zoomControl={false}
          bounds={toBounds(getBounds(mapData, user, display, routingRoutes))}
          boundsOptions={getMapSettings()}
          useFly
          maxZoom={MAX_MAP_ZOOM}
          minZoom={MIN_MAP_ZOOM}
          whenCreated={setMap}
        >
          {allowMarkers && oAuth && (
            <LocationSelector oAuth={oAuth} display={display} allowRightClick={currentStep === 2} />
          )}
          {showMapAreas ? (
            <MapAreas
              areaPolygon={areaPolygon}
              onClick={handleOnSelect}
              groupsToShow={groupsToShow}
              selectedTour={display.selectedTour}
              tourData={tourData}
            />
          ) : null}
          <TileLayer
            attribution="Here Maps"
            url={getMapUrl(url, translations.map.language, ISO2Code, hasPView, satelliteView)}
            ref={mapLayer}
            maxNativeZoom={MAX_NATIVE_ZOOM}
            maxZoom={MAX_MAP_ZOOM}
            eventHandlers={{
              tileerror: () => {
                accessLogging(false, url, 'apiKey', null);
              },
              error: () => {
                accessLogging(false, url, 'apiKey', null);
              },
              locationerror: () => {
                accessLogging(false, url, 'apiKey', null);
              },
            }}
          />
          <ZoomControl position={zoomPosition} zoomInTitle="" zoomOutTitle="" />
          <ScaleControl
            position="bottomright"
            metric={userDistance === 'metric'}
            imperial={userDistance === 'imperial'}
          />
          {mapData && oAuth && (
            <MapMarkers
              mapData={mapData}
              map={map}
              user={user}
              display={display}
              routingRoutes={routingRoutes}
              routingData={routingData}
              tourData={tourData}
              isSolution={isSolution}
              activateTour={activateTour}
              showUnassigned={showUnassigned}
              oAuth={oAuth}
              tourPlanner={tourPlanner}
              solution={solution}
              orders={orders}
            />
          )}
          {isSolution && !hideRoutes && (
            <MapTourLines
              mapData={mapData}
              user={user}
              display={display}
              routingRoutes={routingRoutes}
              routingData={routingData}
              tourData={tourData}
              activateTour={activateTour}
              originalLocation={originalLocation}
            />
          )}
          {isSolution && user.mapSettings.showStops && (
            <ActivityConnectionLines mapData={mapData} display={display} user={user} />
          )}
          {trafficMatrix && showTraffic && currentTour && (
            <>
              <TrafficRouteSegments
                trafficMatrix={trafficMatrix}
                selectedTimestamp={selectedTimestamp}
                routingRoutes={routingRoutes}
                tourData={tourData}
                selectedTour={effectiveTourIndex}
              />
              <TrafficLegend show={!!trafficMatrix} />
            </>
          )}
        </MapContainer>
      </StyledMap>
      {showTrafficTimeline && trafficMatrix && showTraffic && currentTour && (
        <TrafficTimeline
          trafficMatrix={trafficMatrix}
          selectedTimestamp={selectedTimestamp}
          onTimestampChange={setSelectedTimestamp}
          onClose={() => setShowTrafficTimeline(false)}
        />
      )}
    </>
  );
};

export default withTranslation(Map);
